import {
  Button,
  Heading,
  Stack,
  useColorModeValue,
  Link,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Text,
  Image,
  Radio,
  RadioGroup,
  Progress,
  VStack,
  HStack,
  useToast,
  Spinner,
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import {
  useAccount,
  useContractRead,
  useContractWrite,
  usePrepareContractWrite,
  useWaitForTransaction,
} from 'wagmi'
import PollingABI from '../assets/constants/abi/Polling.json'
import NetworkMapping from '../assets/constants/network-mapping.json'
import VotingSvg from '../assets/voting.svg'
import { ethers } from 'ethers'
import { GistDisplay } from '../components'

type PollResult = {
  question: string
  yesVotes: string
  noVotes: string
}

export default function Poll() {
  const pollId = '0'
  const [value, setValue] = useState('')
  const [votes, setVotes] = useState({ yesVotes: 0, noVotes: 0, totalVotes: 0 })
  const { address, isDisconnected } = useAccount()
  const toast = useToast()

  const { data: voted } = useContractRead({
    address: NetworkMapping['80001']['Polling'] as `0x${string}`,
    abi: PollingABI,
    functionName: 'hasVoted',
    args: [ethers.BigNumber.from(pollId), address as `0x${string}`],
    watch: true,
    enabled: !!address,
  })

  const { data: pollResult } = useContractRead({
    address: NetworkMapping['80001']['Polling'] as `0x${string}`,
    abi: PollingABI,
    functionName: 'getResults',
    args: [ethers.BigNumber.from(pollId)],
    watch: true,
  })

  const {
    config,
    error: prepareError,
    isError: isPrepareError,
  } = usePrepareContractWrite({
    address: NetworkMapping['80001']['Polling'] as `0x${string}`,
    abi: PollingABI,
    functionName: 'vote',
    args: [ethers.BigNumber.from(pollId), value === '1' ? true : false],
  })

  const { data, error, isError, write } = useContractWrite(config)

  const { isLoading, isSuccess } = useWaitForTransaction({
    hash: data?.hash,
    onSuccess(data) {
      toast({
        position: 'bottom-right',
        duration: 6000,
        render: () => (
          <Stat bgColor={'brand.50'} py={'3'} px={'8'} borderRadius={'lg'}>
            <StatLabel>Message Updated Successfully</StatLabel>
            <StatHelpText>
              {data && (
                <Link
                  href={`https://mumbai.polygonscan.com/tx/${data?.transactionHash}`}
                  target="_blank"
                >
                  View Transaction
                </Link>
              )}
            </StatHelpText>
          </Stat>
        ),
      })
    },
  })

  useEffect(() => {
    const yesVotes = pollResult && ethers.utils.formatUnits((pollResult as PollResult).yesVotes, 0)
    const noVotes = pollResult && ethers.utils.formatUnits((pollResult as PollResult).noVotes, 0)
    const totalVotes = Number(yesVotes?.toString()) + Number(noVotes?.toString())
    setVotes({
      yesVotes: Number(yesVotes),
      noVotes: Number(noVotes),
      totalVotes: Number(totalVotes),
    })
  }, [pollResult])

  useEffect(() => {
    setValue('')
  }, [isSuccess])

  return (
    <VStack
      spacing={{ base: '30px' }}
      minH={{ base: '80vh', md: '80vh' }}
      wrap={'wrap'}
      align={'center'}
      justify={'center'}
      mb={'10px'}
    >
      <HStack
        minH={{ base: '80vh', md: '80vh' }}
        wrap={'wrap'}
        align={'center'}
        justify={'center'}
        mb={'10px'}
      >
        <Image src={VotingSvg} boxSize={{ base: '220px', md: 'md' }}></Image>

        <Stack
          spacing={{ base: 10, md: 4 }}
          bg={useColorModeValue('white', 'gray.700')}
          rounded={'xl'}
          boxShadow={'lg'}
          p={{ base: '6', md: '20' }}
          my={{ base: '6', md: '12' }}
        >
          <Heading
            lineHeight={1}
            fontSize={{ base: '4xl', md: '4xl' }}
            color={'brand.500'}
            alignSelf={'center'}
          >
            Poll in the Wall 🧱
          </Heading>
          <Heading
            lineHeight={1.2}
            fontSize={{ base: '2xl', md: '2xl' }}
            color={'second.500'}
            alignSelf={'center'}
            textAlign={'center'}
          >
            {`Are you eager to learn Web3?`}
          </Heading>
          <RadioGroup onChange={setValue} value={value} isDisabled={voted as boolean}>
            <Stack direction="row" justifyContent={'space-around'}>
              <Radio size="lg" colorScheme="teal" value={'1'} p={'0px'}>
                <Text fontSize={{ base: 'sm', md: 'lg' }}>Yes! Cant Wait.</Text>
              </Radio>
              <Radio size="lg" colorScheme="red" value={'0'} p={'0px'}>
                <Text fontSize={{ base: 'sm', md: 'lg' }}>Nah! Moving to AI</Text>
              </Radio>
            </Stack>
          </RadioGroup>
          <Stack spacing={6}>
            <Button
              bg={'brand.500'}
              color={'white'}
              _disabled={{
                bg: 'gray.600',
                cursor: 'not-allowed',
              }}
              _hover={{
                bg: 'brand.600',
              }}
              onClick={(e) => {
                e.preventDefault()
                write?.()
              }}
              isDisabled={!write || isLoading}
            >
              {isLoading && <Spinner size="sm" thickness="2px" color="white" mx={'3px'} />}
              {isDisconnected
                ? 'Wallet Not Connected'
                : isLoading
                ? 'Couting Votes...'
                : (voted as boolean)
                ? 'Already Voted'
                : 'Submit Poll'}
            </Button>
            {(isSuccess || (voted as boolean)) && (
              <Stat>
                <StatLabel mb={'5px'}>Total Votes: {votes.totalVotes}</StatLabel>
                <StatNumber mb={'5px'}>
                  <StatLabel>{`${((votes.yesVotes * 100) / votes.totalVotes).toFixed(
                    2
                  )}%`}</StatLabel>
                  <Progress
                    value={(votes.yesVotes * 100) / votes.totalVotes}
                    size="lg"
                    colorScheme="teal"
                    borderRadius={'10px'}
                  />
                </StatNumber>
                <StatNumber mb={'5px'}>
                  <StatLabel>{`${((votes.noVotes * 100) / votes.totalVotes).toFixed(
                    2
                  )}%`}</StatLabel>
                  <Progress
                    value={(votes.noVotes * 100) / votes.totalVotes}
                    size="lg"
                    colorScheme="red"
                    borderRadius={'10px'}
                  />
                </StatNumber>
              </Stat>
            )}
            {(isPrepareError || isError) && (voted as boolean) === false && (
              <div>Error: {(prepareError || error)?.message}</div>
            )}
          </Stack>
        </Stack>
      </HStack>
      <GistDisplay id={'40671a7e33573ea9144e7ff3cc13b422'} />
    </VStack>
  )
}
